import React, { useState } from 'react';
import { X } from 'lucide-react';

interface ComingSoonModalProps {
  isOpen: boolean;
  onClose: () => void;
  modalType?: 'coming-soon' | 'contact' | 'help' | 'privacy-terms';
}

const ComingSoonModal: React.FC<ComingSoonModalProps> = ({ isOpen, onClose, modalType = 'coming-soon' }) => {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    setLoading(true);
    setError('');

    try {
      const response = await fetch('/api/notify-signup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });

      if (!response.ok) {
        throw new Error('Failed to sign up');
      }

      setSubmitted(true);
      setEmail('');
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const renderContent = () => {
    switch (modalType) {
      case 'contact':
        return (
          <>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Contact Us</h2>
            <p className="text-gray-600 mb-6">
              Have a question or need help? We'd love to hear from you!
            </p>
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <p className="text-gray-700 font-medium mb-2">Email us at:</p>
              <a
                href="mailto:info@holein1app.com"
                className="text-lg font-semibold"
                style={{ color: '#9CB75A' }}
              >
                info@holein1app.com
              </a>
            </div>
          </>
        );

      case 'help':
        return (
          <>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Help Center</h2>
            <p className="text-gray-600 mb-6">
              Our help center is coming soon! In the meantime, feel free to reach out to us directly.
            </p>
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <p className="text-gray-700 font-medium mb-2">Need help? Email us at:</p>
              <a
                href="mailto:info@holein1app.com"
                className="text-lg font-semibold"
                style={{ color: '#9CB75A' }}
              >
                info@holein1app.com
              </a>
            </div>
          </>
        );

      case 'privacy-terms':
        return (
          <>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Privacy Policy & Terms</h2>
            <p className="text-gray-600 mb-6">
              Our Privacy Policy and Terms of Service are currently being finalized. We're working hard to ensure your privacy and rights are protected.
            </p>
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <p className="text-gray-700">
                These documents will be available before our app launch. If you have any questions in the meantime, please contact us.
              </p>
            </div>
          </>
        );

      default:
        return (
          <>
            <div className="w-32 h-32 mx-auto mb-4 flex items-center justify-center">
              <img
                src="/branding/hole-in-one-logo-clean.png"
                alt="Hole In 1 Logo"
                className="max-w-full max-h-full object-contain"
              />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Coming Soon</h2>
            <p className="text-gray-600 mb-4">
              We're putting the finishing touches on the app. Enter your email to be notified when we launch!
            </p>
            {submitted ? (
              <div className="rounded-lg p-4" style={{ backgroundColor: '#9CB75A20', color: '#9CB75A' }}>
                Thanks! We'll let you know when we launch.
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                <div className="flex flex-col gap-3">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-gray-500"
                    required
                    disabled={loading}
                  />
                  {error && (
                    <p className="text-red-600 text-sm">{error}</p>
                  )}
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-6 py-3 text-white font-semibold rounded-lg transition-colors hover:opacity-90 disabled:opacity-50"
                    style={{ backgroundColor: '#9CB75A' }}
                  >
                    {loading ? 'Signing up...' : 'Notify Me'}
                  </button>
                </div>
              </form>
            )}
          </>
        );
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-sm w-full p-6 sm:p-8 relative shadow-xl">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <X size={24} />
        </button>

        <div className="text-center">
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default ComingSoonModal;
